import {
  HEATS_PREFETCH_COUNT,
  HeatsTimeRangeContext,
  HeatsTimeRangeContextType,
} from "components/strategyDashboard/heats/context/HeatsTimeRangeContext";
import dayjs, { Dayjs } from "dayjs";
import { useShifts } from "hooks/shifts/useShifts";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { TimeRange } from "hooks/usePrecachedHeats";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { createContext, ReactNode, useCallback, use, useMemo } from "react";
import { CraneShift } from "src/store/api/generatedApi";

export interface ShiftNavigationContextType {
  selectedShift: CraneShift | null;
  canGoBackward: boolean;
  canGoForward: boolean;
  goToPreviousShift: () => void;
  goToNextShift: () => void;
  goToTimestamp: (timestamp: Dayjs) => Promise<void>;
  goToDate: (date: Dayjs) => Promise<void>;
  isFetching: boolean;
  isLoading: boolean;
}

const ShiftNavigationContext = createContext<ShiftNavigationContextType | null>(
  null
);

export const useShiftNavigation = (): ShiftNavigationContextType | null => {
  return use(ShiftNavigationContext);
};

interface ShiftNavigationProviderProps {
  children: ReactNode;
}

export const ShiftNavigationProvider = ({
  children,
}: ShiftNavigationProviderProps) => {
  const { localTimezone } = useLocalDatetime();

  const [shiftStartTime, setShiftStartTime] = useStringQueryParamState<
    string | null
  >("shift", null);

  const {
    shifts,
    selectedShift,
    selectedIndex: selectedShiftIndex,
    canGoBack,
    canGoForward,
    goToPreviousShift,
    goToNextShift,
    goToTimestamp,
    goToDate: goToDateWithTimezone,
    isLoading,
    isError,
    isFetching,
  } = useShifts({
    initialShiftStartTime: shiftStartTime,
    onShiftChange: setShiftStartTime,
    bufferSize: HEATS_PREFETCH_COUNT * 2,
  });

  // Wrap goToDate to inject localTimezone
  const goToDate = useCallback(
    (date: Dayjs) => goToDateWithTimezone(date, localTimezone),
    [goToDateWithTimezone, localTimezone]
  );

  const timeRange = useMemo((): TimeRange => {
    if (selectedShift) {
      return {
        start: selectedShift.shift_start_time,
        end: selectedShift.shift_end_time,
      };
    }
    if (shiftStartTime && dayjs(shiftStartTime).isValid()) {
      return {
        start: shiftStartTime,
        // Optimistically assume 2 shifts per day
        end: dayjs(shiftStartTime).add(12, "hour").toISOString(),
      };
    }
    const now = dayjs().tz(localTimezone).toISOString();
    return { start: now, end: now };
  }, [selectedShift, shiftStartTime, localTimezone]);

  const prefetchRanges = useMemo(
    (): TimeRange[] =>
      shifts
        .slice(
          Math.max(0, selectedShiftIndex - HEATS_PREFETCH_COUNT),
          selectedShiftIndex
        )
        .map((shift) => ({
          start: shift.shift_start_time,
          end: shift.shift_end_time,
        })),
    [shifts, selectedShiftIndex]
  );

  const timeRangeValue: HeatsTimeRangeContextType = useMemo(
    () => ({
      viewMode: "shift" as const,
      timeRange,
      prefetchRanges,
      filterMode: "overlap" as const,
      isLoading: isLoading || selectedShift === null,
      isError,
    }),
    [timeRange, prefetchRanges, isLoading, isError, selectedShift]
  );

  const shiftNavValue: ShiftNavigationContextType = useMemo(
    () => ({
      selectedShift,
      canGoBackward: canGoBack,
      canGoForward,
      goToPreviousShift,
      goToNextShift,
      goToTimestamp,
      goToDate,
      isFetching,
      isLoading,
    }),
    [
      selectedShift,
      canGoBack,
      canGoForward,
      goToPreviousShift,
      goToNextShift,
      goToTimestamp,
      goToDate,
      isFetching,
      isLoading,
    ]
  );

  return (
    <HeatsTimeRangeContext value={timeRangeValue}>
      <ShiftNavigationContext value={shiftNavValue}>
        {children}
      </ShiftNavigationContext>
    </HeatsTimeRangeContext>
  );
};
